/**
 * SKORPIO catalog — single source of truth for structural data.
 *
 * Every value in this file is transcribed from `catalog-notes.md`, which is
 * transcribed from the source PDFs in `catalog/`. Do NOT edit prices in here
 * by hand — fill PRICE in Phase 1B after Phase 2 assertions are in place.
 *
 * Phase 1A (this file): structural data — finishes, collections, sizes,
 *   compatibility, SKU formula. PRICE stub is empty.
 * Phase 2: validate-prices.mjs writes one assertEquals per catalog cell.
 * Phase 1B: PRICE is populated until every assertion passes.
 */

// ═══════════════════════════════════════════════════════════════════════════
//  BASES — 7 codes in 3 price tiers (catalog-notes §2)
// ═══════════════════════════════════════════════════════════════════════════

export const BASE_FINISHES = [
  // Tier A — cheapest (embossed lacquered steel, dark)
  { code: 'GFM69', tier: 'A', name: 'Graphite',       hex: '#3a3a3a' },
  { code: 'GFM73', tier: 'A', name: 'Black',          hex: '#1a1a1a' },
  // Tier B — mid (embossed lacquered steel, neutral)
  { code: 'GFM11', tier: 'B', name: 'Titanium',       hex: '#82827e' },
  { code: 'GFM18', tier: 'B', name: 'Bronze',         hex: '#7d6845' },
  { code: 'GFM70', tier: 'B', name: 'Pearl',          hex: '#d3ccbf' },
  // Tier C — premium (brushed lacquered steel)
  { code: 'BBR',   tier: 'C', name: 'Brushed Bronze', hex: '#a8835a' },
  { code: 'BGR',   tier: 'C', name: 'Brushed Grey',   hex: '#787c80' },
];

export const BASE_BY_CODE = Object.fromEntries(BASE_FINISHES.map(b => [b.code, b]));

export const BASE_TIERS = ['A', 'B', 'C'];

// ═══════════════════════════════════════════════════════════════════════════
//  TOPS — by family (catalog-notes §3)
// ═══════════════════════════════════════════════════════════════════════════

export const GLASS_TOPS = [
  { code: 'CLR',   name: 'Clear glass',                          family: 'glass' },
  { code: 'EXC',   name: 'Extra clear glass',                    family: 'glass' },
  { code: 'EXCB',  name: 'Extra clear bevelled glass',           family: 'glass' },
  { code: 'EXCBR', name: 'Extra clear reversed-bevelled glass',  family: 'glass' },
  { code: 'MST',   name: 'Extra clear bevelled MIST',            family: 'glass' },
];

export const CRYSTALART_TOPS = [
  { code: 'CY01', name: 'CrystalArt — colour 01', family: 'crystalart' },
  { code: 'CY02', name: 'CrystalArt — colour 02', family: 'crystalart' },
];

export const CERAMIC_TOPS = [
  { code: 'KM02', name: 'Marmi Alabastro',             finish: 'matt'   },
  { code: 'KM05', name: 'matt Golden Calacatta',       finish: 'matt'   },
  { code: 'KM06', name: 'glossy Golden Calacatta',     finish: 'glossy' },
  { code: 'KM07', name: 'matt Portoro',                finish: 'matt'   },
  { code: 'KM08', name: 'glossy Portoro',              finish: 'glossy' },
  { code: 'KM09', name: 'glossy Sahara Noir',          finish: 'glossy' },
  { code: 'KM11', name: 'Makalu',                      finish: 'matt'   },
  { code: 'KM12', name: 'Breccia',                     finish: 'matt'   },
  { code: 'KM14', name: 'Corcovado',                   finish: 'matt'   },
  { code: 'KM16', name: 'Kaindy',                      finish: 'matt'   },
  { code: 'KM18', name: 'matt Borghini Calacatta',     finish: 'matt'   },
  { code: 'KM19', name: 'glossy Borghini Calacatta',   finish: 'glossy' },
  { code: 'KM21', name: 'Colosseo',                    finish: 'matt'   },
  { code: 'KM22', name: 'Andromeda',                   finish: 'matt'   },
  { code: 'KM24', name: 'matt Invisible',              finish: 'matt'   },
  { code: 'KS23', name: 'Luxor',                       finish: 'matt'   },
];

export const WOOD_TOPS = [
  { code: 'NC', name: 'Canaletto walnut', family: 'wood' },
  { code: 'RB', name: 'burned oak',       family: 'wood' },
  { code: 'RN', name: 'natural oak',      family: 'wood' },  // WD-S only
];

// Premium brushed profile (KP / KPR). Same codes as tier C bases.
export const PROFILE_FINISHES = [
  { code: 'BBR', name: 'Brushed Bronze profile' },
  { code: 'BGR', name: 'Brushed Grey profile' },
];

// ─── Per-collection ceramic availability (catalog-notes §3.2) ─────────────

const ALL_CERAMICS = CERAMIC_TOPS.map(c => c.code);
const PREMIUM_CERAMICS = ALL_CERAMICS.filter(c => !['KM14', 'KM16'].includes(c));
const ROUND_CERAMICS   = ALL_CERAMICS.filter(c => !['KM14', 'KM16', 'KM19'].includes(c));

export const CERAMIC_AVAILABILITY = {
  KR:  ALL_CERAMICS,      // all 16
  KP:  PREMIUM_CERAMICS,  // 14 — excludes KM14, KM16
  KPR: ROUND_CERAMICS,    // 13 — excludes KM14, KM16, KM19
  KRD: ROUND_CERAMICS,    // 13
  KW:  ROUND_CERAMICS,    // 13 (as ceramic insert under wood top)
};

// ═══════════════════════════════════════════════════════════════════════════
//  COLLECTIONS (catalog-notes §4)
// ═══════════════════════════════════════════════════════════════════════════

export const COLLECTIONS = [
  { code: 'CL',  name: 'SKORPIO',                       year: 2014, topFamily: 'glass',                       legacy: false },
  { code: 'CA',  name: 'SKORPIO Crystalart',            year: 2019, topFamily: 'crystalart',                  legacy: false },
  { code: 'KR',  name: 'SKORPIO Keramik',               year: 2015, topFamily: 'ceramic',                     legacy: false },
  { code: 'KP',  name: 'SKORPIO Keramik Premium',       year: 2019, topFamily: 'ceramic_with_profile',        legacy: false },
  { code: 'KPR', name: 'SKORPIO Keramik Premium Round', year: 2023, topFamily: 'ceramic_with_profile',        legacy: false },
  { code: 'KRD', name: 'SKORPIO Keramik Round',         year: 2016, topFamily: 'ceramic',                     legacy: true  },
  { code: 'KW',  name: 'SKORPIO Ker-Wood Round',        year: 2018, topFamily: 'wood_with_ceramic_insert',    legacy: false },
  { code: 'RND', name: 'SKORPIO Round',                 year: 2014, topFamily: 'mixed',                       legacy: false },
  { code: 'WD',  name: 'SKORPIO Wood',                  year: 2014, topFamily: 'wood',                        legacy: false },
];

export const COLLECTION_BY_CODE = Object.fromEntries(COLLECTIONS.map(c => [c.code, c]));

// ═══════════════════════════════════════════════════════════════════════════
//  SIZES PER COLLECTION (catalog-notes §5)
//
//  Key convention: variant ALWAYS comes BEFORE size, dot-separated.
//    e.g. 'sag.200x120', 'biscuit.220x120', 'polygon.300x130',
//         'S.300x120', 'B.250x128', 'M.300x128', 'T.ø180'.
//  Bare 'WxD' or 'øN' when no variant.
//
//  Each entry: { key, w/d or diameter, h, shape, tops, [variant], [...notes] }
// ═══════════════════════════════════════════════════════════════════════════

// ─── CL — SKORPIO classic glass (p.80-81) ─────────────────────────────────
// Height fixed: 74cm. Glass 15mm except where glassThicknessMm: 12.
export const CL_SIZES = [
  { key: '150x150',     w: 150, d: 150, h: 74, shape: 'rect',   tops: ['CLR', 'EXC'] },
  { key: '200x200',     w: 200, d: 200, h: 74, shape: 'rect',   tops: ['CLR', 'EXC'] },
  { key: '160x90',      w: 160, d:  90, h: 74, shape: 'rect',   tops: ['CLR', 'EXC'], glassThicknessMm: 12 },
  { key: '182x90',      w: 182, d:  90, h: 74, shape: 'rect',   tops: ['CLR', 'EXC'], glassThicknessMm: 12 },
  { key: '200x100',     w: 200, d: 100, h: 74, shape: 'rect',   tops: ['CLR', 'EXC'] },
  { key: '200x106',     w: 200, d: 106, h: 74, shape: 'rect',   tops: ['CLR', 'EXC'], glassThicknessMm: 12 },
  { key: 'sag.200x120', w: 200, d: 120, h: 74, shape: 'shaped', tops: ['CLR', 'EXC', 'EXCB', 'MST'], variant: 'sag' },
  { key: '240x100',     w: 240, d: 100, h: 74, shape: 'rect',   tops: ['CLR', 'EXC'] },
  { key: '240x120',     w: 240, d: 120, h: 74, shape: 'rect',   tops: ['CLR', 'EXC', 'EXCBR'] },
  { key: 'sag.240x120', w: 240, d: 120, h: 74, shape: 'shaped', tops: ['CLR', 'EXC', 'EXCB', 'MST'], variant: 'sag' },
  { key: '300x100',     w: 300, d: 100, h: 74, shape: 'rect',   tops: ['CLR', 'EXC'] },
  { key: '300x120',     w: 300, d: 120, h: 74, shape: 'rect',   tops: ['CLR', 'EXC', 'EXCBR'] },
  { key: 'sag.300x120', w: 300, d: 120, h: 74, shape: 'shaped', tops: ['CLR', 'EXC', 'EXCB', 'MST'], variant: 'sag' },
  { key: 'sag.300x130', w: 300, d: 130, h: 74, shape: 'shaped', tops: ['EXCB', 'MST'],               variant: 'sag' },
];

// ─── CA — SKORPIO Crystalart (p.82) ───────────────────────────────────────
// Height 74cm. CY01 = CY02 in price.
export const CA_SIZES = [
  { key: '200x100',     w: 200, d: 100, h: 74, shape: 'rect',   tops: ['CY01', 'CY02'] },
  { key: 'sag.200x120', w: 200, d: 120, h: 74, shape: 'shaped', tops: ['CY01', 'CY02'], variant: 'sag' },
  { key: '240x120',     w: 240, d: 120, h: 74, shape: 'rect',   tops: ['CY01', 'CY02'] },
  { key: 'sag.240x120', w: 240, d: 120, h: 74, shape: 'shaped', tops: ['CY01', 'CY02'], variant: 'sag' },
  { key: '300x120',     w: 300, d: 120, h: 74, shape: 'rect',   tops: ['CY01', 'CY02'] },
  { key: 'sag.300x120', w: 300, d: 120, h: 74, shape: 'shaped', tops: ['CY01', 'CY02'], variant: 'sag' },
];

// ─── KR — SKORPIO Keramik (p.83) ──────────────────────────────────────────
// Height 75cm. All 16 ceramics same price within size.
export const KR_SIZES = [
  { key: '200x106',         w: 200, d: 106, h: 75, shape: 'rect',    tops: ALL_CERAMICS },
  { key: 'sag.200x120',     w: 200, d: 120, h: 75, shape: 'shaped',  tops: ALL_CERAMICS, variant: 'sag' },
  { key: 'biscuit.220x120', w: 220, d: 120, h: 75, shape: 'biscuit', tops: ALL_CERAMICS, variant: 'biscuit' },
  { key: '240x120',         w: 240, d: 120, h: 75, shape: 'rect',    tops: ALL_CERAMICS },
  { key: 'sag.240x120',     w: 240, d: 120, h: 75, shape: 'shaped',  tops: ALL_CERAMICS, variant: 'sag' },
  { key: '300x120',         w: 300, d: 120, h: 75, shape: 'rect',    tops: ALL_CERAMICS },
  { key: 'sag.300x120',     w: 300, d: 120, h: 75, shape: 'shaped',  tops: ALL_CERAMICS, variant: 'sag' },
  { key: 'polygon.300x130', w: 300, d: 130, h: 75, shape: 'polygon', tops: ALL_CERAMICS, variant: 'polygon' },
  { key: 'sag.320x138',     w: 320, d: 138, h: 75, shape: 'shaped',  tops: ALL_CERAMICS, variant: 'sag' },
];

// ─── KP — SKORPIO Keramik Premium (p.84) ──────────────────────────────────
// Top is 5cm thick on 200x120/240x120, 6cm on 300x128 (which raises h to 76.5).
// Brushed profile (BBR or BGR) is required but price-neutral.
export const KP_SIZES = [
  { key: '200x120', w: 200, d: 120, h: 75,   shape: 'shaped', tops: PREMIUM_CERAMICS, topThicknessCm: 5,
    profiles: ['BBR', 'BGR'] },
  { key: '240x120', w: 240, d: 120, h: 75,   shape: 'shaped', tops: PREMIUM_CERAMICS, topThicknessCm: 5,
    profiles: ['BBR', 'BGR'] },
  { key: '300x128', w: 300, d: 128, h: 76.5, shape: 'shaped', tops: PREMIUM_CERAMICS, topThicknessCm: 6,
    profiles: ['BBR', 'BGR'] },
];

// ─── KPR — SKORPIO Keramik Premium Round (p.85) ───────────────────────────
export const KPR_SIZES = [
  { key: 'ø160', diameter: 160, h: 73.5, shape: 'round', tops: ROUND_CERAMICS,
    profiles: ['BBR', 'BGR'] },
];

// ─── KRD — SKORPIO Keramik Round legacy (p.85, EOL) ───────────────────────
export const KRD_SIZES = [
  { key: 'ø140', diameter: 140, h: 75, shape: 'round', tops: ROUND_CERAMICS, lazySusanAvailable: true },
  { key: 'ø158', diameter: 158, h: 75, shape: 'round', tops: ROUND_CERAMICS, lazySusanAvailable: true },
];

// ─── KW — SKORPIO Ker-Wood Round (p.86) ───────────────────────────────────
// Wood host (NC|RB) + ceramic insert (any of 13). 06 black chrome details (fixed).
// T-variant: swiveling ceramic insert.
export const KW_SIZES = [
  { key: 'ø160',   diameter: 160, h: 75, shape: 'round', tops: ['NC', 'RB'], inserts: ROUND_CERAMICS },
  { key: 'ø180',   diameter: 180, h: 75, shape: 'round', tops: ['NC', 'RB'], inserts: ROUND_CERAMICS },
  { key: 'ø220',   diameter: 220, h: 75, shape: 'round', tops: ['NC', 'RB'], inserts: ROUND_CERAMICS },
  { key: 'T.ø160', diameter: 160, h: 75, shape: 'round', tops: ['NC', 'RB'], inserts: ROUND_CERAMICS, variant: 'T' },
  { key: 'T.ø180', diameter: 180, h: 75, shape: 'round', tops: ['NC', 'RB'], inserts: ROUND_CERAMICS, variant: 'T' },
  { key: 'T.ø220', diameter: 220, h: 75, shape: 'round', tops: ['NC', 'RB'], inserts: ROUND_CERAMICS, variant: 'T' },
];

// ─── RND — SKORPIO Round (p.87) ───────────────────────────────────────────
// ø140 wood includes ballast on base. See availableBases() for tier restriction.
export const RND_SIZES = [
  { key: 'ø130', diameter: 130, h: 75, shape: 'round', tops: ['CLR', 'EXCB'] },
  { key: 'ø140', diameter: 140, h: 75, shape: 'round', tops: ['CLR', 'EXCB', 'NC', 'RB'], lazySusanAvailable: true },
  { key: 'ø160', diameter: 160, h: 75, shape: 'round', tops: ['CLR', 'EXCB', 'NC', 'RB'], lazySusanAvailable: true },
  { key: 'ø180', diameter: 180, h: 75, shape: 'round', tops: ['CLR', 'EXCB', 'NC', 'RB'], lazySusanAvailable: true },
];

// ─── WD — SKORPIO Wood (p.88) ─────────────────────────────────────────────
// Variants S (irregular solid edge), B (barrel), M (Masterwood boat-shaped).
// Y-variant excluded entirely — catalog price cells empty (catalog-notes §10).
export const WD_SIZES = [
  // S — NC / RB / RN, 4cm thick, h75
  { key: 'S.200x100', w: 200, d: 100, h: 75, shape: 'rect',   tops: ['NC', 'RB', 'RN'], variant: 'S', topThicknessCm: 4 },
  { key: 'S.240x120', w: 240, d: 120, h: 75, shape: 'rect',   tops: ['NC', 'RB', 'RN'], variant: 'S', topThicknessCm: 4 },
  { key: 'S.300x120', w: 300, d: 120, h: 75, shape: 'rect',   tops: ['NC', 'RB', 'RN'], variant: 'S', topThicknessCm: 4 },
  // B — barrel top with angled edges, NC / RB
  { key: 'B.200x120', w: 200, d: 120, h: 75, shape: 'barrel', tops: ['NC', 'RB'], variant: 'B' },
  { key: 'B.250x128', w: 250, d: 128, h: 75, shape: 'barrel', tops: ['NC', 'RB'], variant: 'B' },
  { key: 'B.300x128', w: 300, d: 128, h: 75, shape: 'barrel', tops: ['NC', 'RB'], variant: 'B' },
  // M — Masterwood, boat-shaped diagonal planks, NC / RB
  { key: 'M.250x128', w: 250, d: 128, h: 76, shape: 'boat',   tops: ['NC', 'RB'], variant: 'M' },
  { key: 'M.300x128', w: 300, d: 128, h: 76, shape: 'boat',   tops: ['NC', 'RB'], variant: 'M' },
  // Y — bevelled dual-tone (NC with RB details, or RB with NC details), h76
  // Catalog p.98 EUR — m³=0 means non-standard packaging (ships custom). Prices present.
  { key: 'Y.240x120', w: 240, d: 120, h: 76, shape: 'bevelled-dual', tops: ['NC', 'RB'], variant: 'Y' },
  { key: 'Y.300x135', w: 300, d: 135, h: 76, shape: 'bevelled-dual', tops: ['NC', 'RB'], variant: 'Y' },
];

export const SIZES_BY_COLLECTION = {
  CL:  CL_SIZES,
  CA:  CA_SIZES,
  KR:  KR_SIZES,
  KP:  KP_SIZES,
  KPR: KPR_SIZES,
  KRD: KRD_SIZES,
  KW:  KW_SIZES,
  RND: RND_SIZES,
  WD:  WD_SIZES,
};

// ═══════════════════════════════════════════════════════════════════════════
//  COMPATIBILITY (catalog-notes §7)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Which base codes are allowed for a given (collection, sizeKey, topCode)?
 * Default: all 7 base codes.
 * Special-case: RND with wood top (NC or RB) at ø140 — only tier A bases.
 */
export function availableBases(collection, sizeKey, topCode) {
  // Catalog p.87: RND ø140 with wood top is offered only at tier A (GFM69/GFM73).
  // Per Olexandra 2026-05-13: combination not offered in tiers B/C.
  if (collection === 'RND' && sizeKey === 'ø140' && (topCode === 'NC' || topCode === 'RB')) {
    return BASE_FINISHES.filter(b => b.tier === 'A').map(b => b.code);
  }
  return BASE_FINISHES.map(b => b.code);
}

/**
 * Which top codes are allowed for a (collection, sizeKey)?
 * Reads from the size entry's `tops` array.
 */
export function availableTops(collection, sizeKey) {
  const entry = SIZES_BY_COLLECTION[collection]?.find(s => s.key === sizeKey);
  return entry?.tops ?? [];
}

/**
 * Which ceramic inserts (KW) or brushed profiles (KP/KPR) are allowed?
 */
export function availableInsertsOrProfiles(collection, sizeKey) {
  const entry = SIZES_BY_COLLECTION[collection]?.find(s => s.key === sizeKey);
  if (collection === 'KW') return entry?.inserts ?? [];
  if (collection === 'KP' || collection === 'KPR') return entry?.profiles ?? [];
  return [];
}

// ═══════════════════════════════════════════════════════════════════════════
//  PRICING (catalog-notes §6)
//
//  Shape: PRICE[collection][sizeKey][topGroup][tier] = GBP inc. VAT, integer.
//
//  topGroup encoding:
//    CL  : 'CLR' | 'EXC' | 'EXCB' | 'MST'
//    CA  : 'CY'
//    KR  : 'ceramic'   (all 16 same price within size)
//    KP  : 'ceramic'   (14)
//    KPR : 'ceramic'   (13)
//    KRD : 'ceramic'   (13)
//    KW  : 'ceramic'   (wood host + insert priced as single group per size+T)
//    RND : 'CLR' | 'EXCB' | 'NC_RB'
//    WD  : 'wood'      (single column per size_key; variant encoded in key)
//
//  Empty for Phase 1A — Phase 2 writes assertions first, then Phase 1B fills.
// ═══════════════════════════════════════════════════════════════════════════

export const PRICE = {

  // ─── CL — SKORPIO classic glass (EUR price list p.91-92) ─────────────
  CL: {
    '150x150':     { CLR: { A: 3801, B: 3967, C: 4910 }, EXC: { A: 3925, B: 4091, C: 5034 } },
    '200x200':     { CLR: { A: 4868, B: 5034, C: 5978 }, EXC: { A: 5016, B: 5183, C: 6127 } },
    '160x90':      { CLR: { A: 2224, B: 2412, C: 3212 }, EXC: { A: 2303, B: 2492, C: 3291 } },
    '182x90':      { CLR: { A: 2346, B: 2530, C: 3334 }, EXC: { A: 2434, B: 2620, C: 3422 } },
    '200x100':     { CLR: { A: 2997, B: 3182, C: 3984 }, EXC: { A: 3087, B: 3272, C: 4072 } },
    '200x106':     { CLR: { A: 2602, B: 2786, C: 3588 }, EXC: { A: 2696, B: 2880, C: 3683 } },
    'sag.200x120': {
      CLR:  { A: 3389, B: 3577, C: 4379 },
      EXC:  { A: 3488, B: 3676, C: 4478 },
      EXCB: { A: 3959, B: 4144, C: 4944 },
      MST:  { A: 3978, B: 4163, C: 4962 },
    },
    '240x100':     { CLR: { A: 3486, B: 3672, C: 4523 }, EXC: { A: 3585, B: 3771, C: 4622 } },
    '240x120': {
      CLR:   { A: 3785, B: 3972, C: 4823 },
      EXC:   { A: 3889, B: 4077, C: 4927 },
      EXCBR: { A: 4380, B: 4564, C: 5419 },
    },
    'sag.240x120': {
      CLR:  { A: 3959, B: 4144, C: 4996 },
      EXC:  { A: 4063, B: 4248, C: 5100 },
      EXCB: { A: 4248, B: 4434, C: 5285 },
      MST:  { A: 4267, B: 4453, C: 5304 },
    },
    '300x100':     { CLR: { A: 3848, B: 4034, C: 4885 }, EXC: { A: 3972, B: 4158, C: 5009 } },
    '300x120': {
      CLR:   { A: 4188, B: 4372, C: 5224 },
      EXC:   { A: 4321, B: 4506, C: 5357 },
      EXCBR: { A: 4891, B: 5078, C: 5930 },
    },
    'sag.300x120': {
      CLR:  { A: 4366, B: 4554, C: 5404 },
      EXC:  { A: 4501, B: 4687, C: 5539 },
      EXCB: { A: 4686, B: 4872, C: 5723 },
      MST:  { A: 4705, B: 4890, C: 5741 },
    },
    'sag.300x130': {
      EXCB: { A: 4859, B: 5046, C: 5896 },
      MST:  { A: 4878, B: 5063, C: 5914 },
    },
  },

  // ─── CA — SKORPIO Crystalart (EUR p.93) ──────────────────────────────
  CA: {
    '200x100':     { CY: { A: 4045, B: 4233, C: 5033 } },
    'sag.200x120': { CY: { A: 4531, B: 4718, C: 5518 } },
    '240x120':     { CY: { A: 4728, B: 4915, C: 5764 } },
    'sag.240x120': { CY: { A: 4862, B: 5048, C: 5898 } },
    '300x120':     { CY: { A: 5636, B: 5824, C: 6674 } },
    'sag.300x120': { CY: { A: 5832, B: 6020, C: 6870 } },
  },

  // ─── KR — SKORPIO Keramik (EUR p.94) ─────────────────────────────────
  KR: {
    '200x106':         { ceramic: { A: 4450, B: 4823, C: 5432 } },
    'sag.200x120':     { ceramic: { A: 4745, B: 5119, C: 5723 } },
    'biscuit.220x120': { ceramic: { A: 4916, B: 5290, C: 5894 } },
    '240x120':         { ceramic: { A: 4939, B: 5314, C: 5922 } },
    'sag.240x120':     { ceramic: { A: 5014, B: 5389, C: 6042 } },
    '300x120':         { ceramic: { A: 6488, B: 6862, C: 7514 } },
    'sag.300x120':     { ceramic: { A: 6575, B: 6948, C: 7605 } },
    'polygon.300x130': { ceramic: { A: 7029, B: 7402, C: 8059 } },
    'sag.320x138':     { ceramic: { A: 7512, B: 7885, C: 8537 } },
  },

  // ─── KP — SKORPIO Keramik Premium (EUR p.95) ─────────────────────────
  KP: {
    '200x120': { ceramic: { A: 5283, B: 5467, C: 6262 } },
    '240x120': { ceramic: { A: 5732, B: 5918, C: 6759 } },
    '300x128': { ceramic: { A: 7696, B: 7881, C: 8721 } },
  },

  // ─── KPR — SKORPIO Keramik Premium Round (NOT in EUR price list) ─────
  // Collection exists (catalog p.85 GBP), but no EUR price in 18885_pricelist.pdf.
  // Surface in UI as "Price on request" until separate EUR pricing arrives.
  // See catalog-notes §10.
  KPR: {},

  // ─── KRD — SKORPIO Keramik Round (EUR p.95) ──────────────────────────
  // Not marked EOL in EUR catalog (legacy flag is GBP-market specific).
  KRD: {
    'ø140': { ceramic: { A: 4073, B: 4210, C: 4811 } },
    'ø158': { ceramic: { A: 4223, B: 4362, C: 4994 } },
  },

  // ─── KW — SKORPIO Ker-Wood Round (EUR p.96) ──────────────────────────
  KW: {
    'ø160':   { ceramic: { A: 5421, B: 5559, C: 6190  } },
    'ø180':   { ceramic: { A: 5675, B: 5813, C: 6443  } },
    'ø220':   { ceramic: { A: 8157, B: 8320, C: 9254  } },
    'T.ø160': { ceramic: { A: 5950, B: 6088, C: 6719  } },
    'T.ø180': { ceramic: { A: 6312, B: 6451, C: 7081  } },
    'T.ø220': { ceramic: { A: 9321, B: 9484, C: 10417 } },
  },

  // ─── RND — SKORPIO Round (EUR p.97) ──────────────────────────────────
  // ø140 NC_RB: tier A only — tiers B/C absent in catalog (same restriction as GBP).
  RND: {
    'ø130': {
      CLR:  { A: 2866, B: 3004, C: 3608 },
      EXCB: { A: 3287, B: 3426, C: 4029 },
    },
    'ø140': {
      CLR:   { A: 3044, B: 3185, C: 3787 },
      EXCB:  { A: 3512, B: 3652, C: 4252 },
      NC_RB: { A: 3865 },
    },
    'ø160': {
      CLR:   { A: 3653, B: 3793, C: 4431 },
      EXCB:  { A: 4193, B: 4334, C: 4973 },
      NC_RB: { A: 4503, B: 4640, C: 5270 },
    },
    'ø180': {
      CLR:   { A: 4240, B: 4380, C: 5020 },
      EXCB:  { A: 4974, B: 5113, C: 5750 },
      NC_RB: { A: 4777, B: 4916, C: 5548 },
    },
  },

  // ─── WD — SKORPIO Wood (EUR p.98) ────────────────────────────────────
  // Y-variant INCLUDED in EUR catalog (was mis-excluded in GBP transcription
  // — re-validation caught the error).
  WD: {
    'S.200x100': { wood: { A: 4770, B: 4956, C: 5748 } },
    'S.240x120': { wood: { A: 5480, B: 5659, C: 6503 } },
    'S.300x120': { wood: { A: 5592, B: 5776, C: 6619 } },
    'B.200x120': { wood: { A: 3842, B: 4027, C: 4821 } },
    'B.250x128': { wood: { A: 4519, B: 4705, C: 5548 } },
    'B.300x128': { wood: { A: 4747, B: 4932, C: 5774 } },
    'M.250x128': { wood: { A: 5453, B: 5636, C: 6476 } },
    'M.300x128': { wood: { A: 6088, B: 6272, C: 7115 } },
    'Y.240x120': { wood: { A: 4450, B: 4634, C: 5478 } },
    'Y.300x135': { wood: { A: 4934, B: 5118, C: 5961 } },
  },

};

/**
 * Given a (collection, topCode), return the pricing-group key.
 * This collapses identically-priced tops (e.g. all 16 ceramics in KR) to one key.
 */
export function topGroupForPrice(collection, topCode) {
  if (collection === 'CL') {
    if (topCode === 'CLR'   || topCode === 'EXC' ||
        topCode === 'EXCB'  || topCode === 'EXCBR' ||
        topCode === 'MST') return topCode;
    return null;
  }
  if (collection === 'CA') {
    if (topCode === 'CY01' || topCode === 'CY02') return 'CY';
    return null;
  }
  if (collection === 'KR' || collection === 'KP' ||
      collection === 'KPR' || collection === 'KRD' || collection === 'KW') {
    return ALL_CERAMICS.includes(topCode) || topCode === 'NC' || topCode === 'RB' ? 'ceramic' : null;
    // KW: top is NC|RB (wood host) but ceramic insert is in `insertOrProfile`; price is per-size, so 'ceramic' group is used.
  }
  if (collection === 'RND') {
    if (topCode === 'CLR')  return 'CLR';
    if (topCode === 'EXCB') return 'EXCB';
    if (topCode === 'NC' || topCode === 'RB') return 'NC_RB';
    return null;
  }
  if (collection === 'WD') {
    if (topCode === 'NC' || topCode === 'RB' || topCode === 'RN') return 'wood';
    return null;
  }
  return null;
}

/**
 * Look up price for a fully-specified configuration.
 * Returns null if any component is missing — that surfaces as a failed assertion.
 */
export function lookupPrice({ collection, sizeKey, topCode, baseCode }) {
  const tier = BASE_BY_CODE[baseCode]?.tier;
  const group = topGroupForPrice(collection, topCode);
  if (!tier || !group) return null;
  return PRICE[collection]?.[sizeKey]?.[group]?.[tier] ?? null;
}

// ═══════════════════════════════════════════════════════════════════════════
//  SKU FORMULA (catalog-notes §1)
//
//  SK.<collection>[.<variant>].<size>.<base>.<top>[.<insertOrProfile>]
//
//  Notes:
//   - sizeKey in this module already encodes the variant when present
//     (e.g. 'sag.200x120' or 'S.300x120'). We pass it through verbatim — the
//     resulting SKU therefore reads e.g. SK.CL.sag.200x120.GFM11.MST.
//   - For KW: top is NC|RB (the wood host), insertOrProfile is the ceramic code.
//   - For KP/KPR: top is the ceramic code, insertOrProfile is BBR|BGR (profile).
// ═══════════════════════════════════════════════════════════════════════════

export function buildSku({ collection, sizeKey, baseCode, topCode, insertOrProfile }) {
  const parts = ['SK', collection, sizeKey, baseCode, topCode];
  if (insertOrProfile) parts.push(insertOrProfile);
  return parts.join('.');
}

// ═══════════════════════════════════════════════════════════════════════════
//  ACCESSORIES (catalog-notes §8)
// ═══════════════════════════════════════════════════════════════════════════

// Lazy Susan price unknown — flag in UI as "Price on request" until a separate
// accessory price list is integrated.
export const ACCESSORY_PRICES = {
  // 'LZS': null,   // Lazy Susan — pending
};

// ═══════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════
//  CHAIRS — BELINDA & BELINDA ML (catalog-notes §12)
//
//  Source: catalog/BELINDA_ProductSheet.pdf + catalog/Cattelan_general_pricelist_EUR.pdf p.145.
//
//  Two collections share the SAME shell + seat dimensions (53.5 × 59 × 86h),
//  differing only by frame material:
//    BL   wood frame (solid ash, 4 stains)
//    BLM  metal frame (embossed lacquered steel, 4 colors)
//
//  Each collection has 2 size variants:
//    'reg' = without armrests, ships 2 pcs/box
//    'B'   = with armrests (Braccioli), ships 1 pcs/box
//
//  Each chair has 4 upholstery groups, each containing many color codes.
//  Price varies by (collection, size, upholstery_group). Frame color is free
//  within the collection (all 4 frame finishes priced equally).
// ═══════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════

export const CHAIR_COLLECTIONS = [
  { code: 'BL',  name: 'BELINDA',    designer: 'Paolo Cattelan', year: 2018, frame: 'wood'  },
  { code: 'BLM', name: 'BELINDA ML', designer: 'Paolo Cattelan', year: 2021, frame: 'metal' },
];

export const CHAIR_COLLECTION_BY_CODE = Object.fromEntries(CHAIR_COLLECTIONS.map(c => [c.code, c]));

// ─── Frame finishes per collection ────────────────────────────────────────

export const CHAIR_FRAMES = {
  BL: [
    { code: 'frN',  name: 'Natural ashwood',                hex: '#d9c2a2' },
    { code: 'frNC', name: 'Canaletto walnut stained ash',   hex: '#5a3a23' },
    { code: 'frRB', name: 'Burned oak stained ash',         hex: '#3a2a1c' },
    { code: 'fr73', name: 'Open pore matt black ashwood',   hex: '#1a1a1a' },
  ],
  BLM: [
    { code: 'GFM11', name: 'Titanium', hex: '#82827e' },
    { code: 'GFM18', name: 'Bronze',   hex: '#7d6845' },
    { code: 'GFM69', name: 'Graphite', hex: '#3a3a3a' },
    { code: 'GFM73', name: 'Black',    hex: '#1a1a1a' },
  ],
};

// ─── Sizes (both collections share the same physical dimensions) ──────────

export const CHAIR_SIZES = [
  { key: 'reg', w: 53.5, d: 59, h: 86, withArms: false, m3: 0.48, pcsPerBox: 2 },
  { key: 'B',   w: 53.5, d: 59, h: 86, withArms: true,  m3: 0.36, pcsPerBox: 1 },
];

// ─── Upholstery — 4 pricing groups, each with many color codes ────────────
// Source: BELINDA_ProductSheet.pdf p.3-6
// COM = Customer Own Material (priced as synthetic_leather)
// COL = Customer Own Leather/fabric (priced as fabric_micronubuck)

export const CHAIR_UPHOLSTERY_GROUPS = ['synthetic_leather', 'fabric_micronubuck', 'soft_leather', 'soft_leather_glove'];

export const CHAIR_UPHOLSTERY_GROUP_LABEL = {
  synthetic_leather:  'Synthetic leather / C.O.M.',
  fabric_micronubuck: 'Fabric / Micro nubuck / C.O.L.',
  soft_leather:       'Soft leather',
  soft_leather_glove: 'Soft leather Glove (premium)',
};

// Short SKU-friendly keys
export const CHAIR_UPHOLSTERY_GROUP_SKU = {
  synthetic_leather:  'SYN',
  fabric_micronubuck: 'FMN',
  soft_leather:       'SL',
  soft_leather_glove: 'SLG',
};

// 31 fabric codes (p.3) — names not provided in catalog
export const CHAIR_FABRICS = [
  'B240','B241','B920','B730','B921','B610','B732','B922','B810','B923',
  'B242','B924','B731','B925','B811','B926','B243','B927','B244','B928',
  'B245','B929','B246','B247','B930','B248','B931','B249','B932','B713','B714',
];

// 14 micro-nubuck codes (p.4)
export const CHAIR_MICRO_NUBUCK = [
  { code: 'MN103', name: 'White'         },
  { code: 'MN100', name: 'Beige'         },
  { code: 'MN806', name: 'Sand'          },
  { code: 'MN604', name: 'Shadow'        },
  { code: 'MN006', name: 'Dusty Rose'    },
  { code: 'MN413', name: 'Almond'        },
  { code: 'MN048', name: 'Sunstone'      },
  { code: 'MN709', name: 'Mint'          },
  { code: 'MN801', name: 'Musk'          },
  { code: 'MN603', name: 'Cognac'        },
  { code: 'MN609', name: 'Marron'        },
  { code: 'MN618', name: 'Lead'          },
  { code: 'MN626', name: 'Charcoal'      },
  { code: 'MN320', name: 'Cardinal'      },
];

// 36 soft leather codes (p.5)
export const CHAIR_SOFT_LEATHER = [
  { code: '971', name: 'Bianco'        },
  { code: '937', name: 'Palladio'      },
  { code: '972', name: 'Canova'        },
  { code: '947', name: 'Oyster'        },
  { code: '959', name: 'Ice'           },
  { code: '939', name: 'Nymph'         },
  { code: '949', name: 'Cenere'        },
  { code: '951', name: 'Castoro'       },
  { code: '993', name: 'Artico'        },
  { code: '938', name: 'Dakota'        },
  { code: '995', name: 'Nardò'         },
  { code: '983', name: 'Argilla'       },
  { code: '948', name: 'Lino'          },
  { code: '969', name: 'Cashmere'      },
  { code: '945', name: 'Safari'        },
  { code: '950', name: 'Tortora'       },
  { code: '960', name: 'Bruno'         },
  { code: '942', name: 'Fango'         },
  { code: '946', name: 'Marrone'       },
  { code: '988', name: 'Tabacco'       },
  { code: '952', name: 'Smoke'         },
  { code: '953', name: 'Ardesia'       },
  { code: '977', name: 'Piombo'        },
  { code: '956', name: 'Atlantico'     },
  { code: '981', name: 'Biscotto'      },
  { code: '982', name: 'Cognac'        },
  { code: '996', name: 'Siena'         },
  { code: '994', name: 'Canyon'        },
  { code: '943', name: 'Moka'          },
  { code: '891', name: 'Testa di Moro' },
  { code: '944', name: 'Bordeaux'      },
  { code: '989', name: 'Rosso Corsa'   },
  { code: '973', name: 'Nero'          },
  { code: '987', name: 'Sherwood'      },
  { code: '940', name: 'Navy'          },
  { code: '974', name: 'Capri'         },
];

// 13 glove (premium) soft leather codes (p.5)
export const CHAIR_SOFT_LEATHER_GLOVE = [
  { code: 'GLV02', name: 'Dover'    },
  { code: 'GLV03', name: 'Ecrù'     },
  { code: 'GLV21', name: 'Juta'     },
  { code: 'GLV07', name: 'Argo'     },
  { code: 'GLV11', name: 'Taupe'    },
  { code: 'GLV14', name: 'Platoon'  },
  { code: 'GLV15', name: 'Carbon'   },
  { code: 'GLV17', name: 'Hornet'   },
  { code: 'GLV22', name: 'Botanico' },
  { code: 'GLV12', name: 'Oliva'    },
  { code: 'GLV18', name: 'Barrique' },
  { code: 'GLV23', name: 'Amarone'  },
  { code: 'GLV20', name: 'Senape'   },
];

// 18 synthetic leather codes (p.6)
export const CHAIR_SYNTHETIC_LEATHER = [
  { code: 'ECP01', name: 'Bianco'        },
  { code: 'ECP72', name: 'Canova'        },
  { code: 'ECP47', name: 'Oyster'        },
  { code: 'ECP48', name: 'Lino'          },
  { code: 'ECP25', name: 'Canapa'        },
  { code: 'ECP05', name: 'Tortora'       },
  { code: 'ECP26', name: 'Fango'         },
  { code: 'ECP83', name: 'Argilla'       },
  { code: 'ECP07', name: 'Marrone'       },
  { code: 'ECP27', name: 'Fossil'        },
  { code: 'ECP08', name: 'Moka'          },
  { code: 'ECP36', name: 'Cemento'       },
  { code: 'ECP23', name: 'London'        },
  { code: 'ECP37', name: 'Ardesia'       },
  { code: 'ECP24', name: 'Nero'          },
  { code: 'ECP22', name: 'Navy'          },
  { code: 'ECP14', name: 'Bordeaux'      },
  { code: 'ECP13', name: 'Rosso Corsa'   },
];

// Custom-supplied material codes (priced as their host group)
export const CHAIR_CUSTOM = [
  { code: 'COM', name: "Customer Own Material",  pricesAs: 'synthetic_leather'  },
  { code: 'COL', name: "Customer Own Leather",   pricesAs: 'fabric_micronubuck' },
];

// Map any upholstery code → its pricing group key
export function chairUpholsteryGroup(code) {
  if (code === 'COM') return 'synthetic_leather';
  if (code === 'COL') return 'fabric_micronubuck';
  if (CHAIR_SYNTHETIC_LEATHER.some(x => x.code === code)) return 'synthetic_leather';
  if (CHAIR_FABRICS.includes(code))                        return 'fabric_micronubuck';
  if (CHAIR_MICRO_NUBUCK.some(x => x.code === code))       return 'fabric_micronubuck';
  if (CHAIR_SOFT_LEATHER.some(x => x.code === code))       return 'soft_leather';
  if (CHAIR_SOFT_LEATHER_GLOVE.some(x => x.code === code)) return 'soft_leather_glove';
  return null;
}

// ─── PRICING (EUR p.145) ──────────────────────────────────────────────────
// Shape: CHAIR_PRICE[collection][sizeKey][upholsteryGroup] = EUR integer.
// Frame finish does NOT affect price within a collection.
export const CHAIR_PRICE = {
  BL: {
    reg: {
      synthetic_leather:   935,
      fabric_micronubuck:  979,
      soft_leather:       1084,
      soft_leather_glove: 1139,
    },
    B: {
      synthetic_leather:  1084,
      fabric_micronubuck: 1155,
      soft_leather:       1291,
      soft_leather_glove: 1358,
    },
  },
  BLM: {
    reg: {
      synthetic_leather:   759,
      fabric_micronubuck:  800,
      soft_leather:        903,
      soft_leather_glove:  955,
    },
    B: {
      synthetic_leather:   903,
      fabric_micronubuck:  972,
      soft_leather:       1102,
      soft_leather_glove: 1168,
    },
  },
};

export function lookupChairPrice({ collection, sizeKey, upholsteryCode }) {
  const group = chairUpholsteryGroup(upholsteryCode);
  if (!group) return null;
  return CHAIR_PRICE[collection]?.[sizeKey]?.[group] ?? null;
}

// ─── SKU FORMULA for chairs ───────────────────────────────────────────────
// Format: CH.<collection>.<size>.<frameCode>.<groupKey>.<upholsteryCode>
//   CH         = product kind prefix (chair)
//   collection = BL | BLM
//   size       = reg | B  (B = with armrests)
//   frameCode  = frN/frNC/frRB/fr73 (BL) or GFM11/GFM18/GFM69/GFM73 (BLM)
//   groupKey   = SYN | FMN | SL | SLG
//   upholstery = COM/COL/B-code/MN-code/ECP-code/numeric-code/GLV-code
//
// Examples:
//   CH.BL.reg.frN.SL.971         BELINDA without arms, natural ash, soft leather Bianco
//   CH.BL.B.frNC.FMN.MN603       BELINDA with arms, Canaletto stain, micro-nubuck Cognac
//   CH.BLM.reg.GFM69.SLG.GLV12   BELINDA ML without arms, graphite, glove leather Oliva
//   CH.BL.B.fr73.SYN.COM         BELINDA with arms, black ash, customer-supplied synthetic-priced material
export function buildChairSku({ collection, sizeKey, frameCode, upholsteryCode }) {
  const group = chairUpholsteryGroup(upholsteryCode);
  const groupKey = CHAIR_UPHOLSTERY_GROUP_SKU[group] || 'X';
  return ['CH', collection, sizeKey, frameCode, groupKey, upholsteryCode].join('.');
}

// ═══════════════════════════════════════════════════════════════════════════
//  PHOTO TEXTURE MANIFEST (catalog-methodology §7 "photo-or-procedural" pattern)
//
//  Lists every (group, code) for which we have a real HD photo swatch on
//  disk under `/public/textures/materials/<group>/<code>.<ext>`. Used by the
//  texture loader to decide between a photo lookup and procedural fallback.
//
//  Source: 148 swatches scraped from Cattelan Italia finishes API
//          (https://www.cattelanitalia.com/en/finishes/index?c=<id>) on
//          2026-05-13. Image-rights: Cattelan Italia originals — keep
//          for in-configurator preview only; do NOT redistribute as standalone.
//
//  Auto-regenerate via: node scripts/regenerate-photo-manifest.mjs   (TODO)
// ═══════════════════════════════════════════════════════════════════════════

export const PHOTO_TEXTURE_MANIFEST = {
  wood:       { NC: 'jpg', RB: 'jpg', frN: 'jpg', frNC: 'jpg', frRB: 'jpg', fr73: 'jpg' },
  ceramic: {
    KM02: 'jpg', KM05: 'jpg', KM06: 'jpg', KM07: 'jpg', KM08: 'jpg', KM09: 'jpg',
    KM11: 'jpg', KM12: 'jpg', KM14: 'jpg', KM16: 'jpg', KM18: 'jpg', KM19: 'jpg',
    KM21: 'jpg', KM22: 'jpg', KM24: 'jpg', KS23: 'jpg',
  },
  crystalart: { CY01: 'jpg', CY02: 'jpg' },
  metal:      { GFM11: 'jpg', GFM18: 'jpg', GFM69: 'jpg', GFM70: 'jpg', GFM73: 'jpg', BBR: 'jpg', BGR: 'jpg' },
  glass:      { CLR: 'jpg', EXC: 'jpg', EXCB: 'jpg', EXCBR: 'jpg', MST: 'jpg' },
  'chair/synthetic-leather': {
    ECP01: 'jpg', ECP05: 'jpg', ECP07: 'jpg', ECP08: 'jpg', ECP13: 'jpg', ECP14: 'jpg',
    ECP22: 'jpg', ECP23: 'jpg', ECP24: 'jpg', ECP25: 'jpg', ECP26: 'jpg', ECP27: 'jpg',
    ECP36: 'jpg', ECP37: 'jpg', ECP47: 'jpg', ECP48: 'jpg', ECP72: 'jpg', ECP83: 'jpg',
  },
  'chair/micro-nubuck': {
    MN006: 'jpg', MN048: 'jpg', MN100: 'jpg', MN103: 'jpg', MN320: 'jpg', MN413: 'jpg',
    MN603: 'jpg', MN604: 'jpg', MN609: 'jpg', MN618: 'jpg', MN626: 'jpg', MN709: 'jpg',
    MN801: 'jpg', MN806: 'jpg',
  },
  'chair/soft-leather': {
    '891': 'jpg', '937': 'jpg', '938': 'jpg', '939': 'jpg', '940': 'jpg', '942': 'jpg',
    '943': 'jpg', '944': 'jpg', '945': 'jpg', '946': 'jpg', '947': 'jpg', '948': 'jpg',
    '949': 'jpg', '950': 'jpg', '951': 'jpg', '952': 'jpg', '953': 'jpg', '956': 'jpg',
    '959': 'jpg', '960': 'jpg', '969': 'jpg', '971': 'jpg', '972': 'jpg', '973': 'jpg',
    '974': 'jpg', '977': 'jpg', '981': 'jpg', '982': 'jpg', '983': 'jpg', '987': 'jpg',
    '988': 'jpg', '989': 'jpg', '993': 'jpg', '994': 'jpg', '995': 'jpg', '996': 'jpg',
  },
  'chair/fabric': {
    B240: 'jpg', B241: 'jpg', B242: 'jpg', B243: 'jpg', B244: 'jpg', B245: 'jpg',
    B246: 'jpg', B247: 'jpg', B248: 'jpg', B249: 'jpg', B610: 'jpg', B713: 'jpg',
    B714: 'jpg', B730: 'jpg', B731: 'jpg', B732: 'jpg', B810: 'jpg', B811: 'jpg',
    B920: 'jpg', B921: 'jpg', B922: 'jpg', B923: 'jpg', B924: 'jpg', B925: 'jpg',
    B926: 'jpg', B927: 'jpg', B928: 'jpg', B929: 'jpg', B930: 'jpg', B931: 'jpg',
    B932: 'jpg',
  },
  'chair/soft-leather-glove': {
    GLV02: 'jpg', GLV03: 'jpg', GLV07: 'jpg', GLV11: 'jpg', GLV12: 'jpg', GLV14: 'jpg',
    GLV15: 'jpg', GLV17: 'jpg', GLV18: 'jpg', GLV20: 'jpg', GLV21: 'jpg', GLV22: 'jpg',
    GLV23: 'jpg',
  },
};

/**
 * Return the in-public URL for a photo swatch, or null if no photo exists.
 * Use this to decide between photo and procedural fallback in the 3D scene.
 */
export function photoTextureUrl(group, code) {
  const ext = PHOTO_TEXTURE_MANIFEST[group]?.[code];
  return ext ? `/textures/materials/${group}/${code}.${ext}` : null;
}
